<template>
  <article>
    <activity-stage
      :customRootContainerClass="`concentration ${keyboardEnabled ? '' : 'full-height'}`"
    >
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <div v-if="viewedIntro" class="top" :class="{ isMobile }">
          <div class="left">
            <div class="card-container" role="list">
              <flippable-card
                v-for="(card, index) of currentRound.leftColumn"
                data-column="left"
                role="listitem"
                :key="card.id"
                :card="card"
                :callback="handleCardFlip"
                :is-flipped="card.flipped"
                :isTyping="keyboardEnabled"
              >
                <div :aria-hidden="card.flipped" slot="card-back" class="back-side">
                  <card-border height="65" width="220"></card-border>
                  <img
                    :alt="`Closed Card, Column 1, Card ${index + 1}`"
                    :src="currentRound.cardBack.image.src"
                  />
                </div>
                <div :aria-hidden="!card.flipped" slot="card-front" class="front-side">
                  <card-border height="65" width="220"></card-border>
                  <div
                    :aria-label="`Open Card, Column 1, Card ${index + 1}, ${card.cardText}`"
                    class="card-text"
                    role="figure"
                  >
                    <span>{{ card.cardText }}</span>
                  </div>
                </div>
              </flippable-card>
            </div>
            <div class="card-container" role="list">
              <flippable-card
                v-for="(card, index) of currentRound.rightColumn"
                data-column="right"
                role="listitem"
                :callback="handleCardFlip"
                :is-flipped="card.flipped"
                :card="card"
                :key="card.id"
                :isTyping="keyboardEnabled"
              >
                <div :aria-hidden="card.flipped" slot="card-back" class="back-side">
                  <card-border height="65" width="220"></card-border>
                  <img
                    :alt="`Closed Card, Column 2, Card ${index + 1}`"
                    :src="currentRound.cardBack.image.src"
                  />
                </div>
                <div :aria-hidden="!card.flipped" slot="card-front" class="front-side">
                  <card-border height="65" width="220"></card-border>
                  <div
                    :aria-label="`Open Card, Column 2, Card ${index + 1}, ${card.cardText}`"
                    class="card-text"
                    role="figure"
                  >
                    <span>{{ card.cardText }}</span>
                  </div>
                </div>
              </flippable-card>
            </div>
          </div>
          <div class="right">
            <div
              v-for="(card, index) of currentRound.rightColumn"
              :key="card.id"
              class="td-container"
            >
              <blinking-cursor-text
                v-if="card.matched"
                :key="card.id"
                :text="card.answer"
                :text-step="index"
              ></blinking-cursor-text>
            </div>
          </div>
        </div>
        <div
          v-if="currentRound.leftColumn && !keyboardEnabled"
          :class="`cards-per-column-${currentRound.leftColumn.length}`"
        >
          <div v-if="currentRound.subtitle" class="subtitle">
            <h2>
              {{ currentRound.subtitle }}
            </h2>
          </div>
          <div class="fact">
            <p>{{ currentRound.fact }}</p>
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import Vue from 'vue';
import { TweenMax, TimelineLite } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';
import AccessibilityHelper from 'mixins/AccessibilityHelper';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import FlippableCard from 'components/activity/FlippableCard.vue';
import CardBorder from 'svg-components/concentration/CardBorder.vue';

export default {
  components: {
    ActivityStage,
    BlinkingCursorText,
    FlippableCard,
    CardBorder
  },

  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, AccessibilityHelper, ErrorLogger],

  data() {
    return {
      activityName: 'Concentration'
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  computed: {
    isEndOfActivity() {
      return this.currentRound?.rightColumn?.every(({ matched }) => matched);
    }
  },

  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
      await this.loadImages();
    },

    async loadImages() {
      this.rounds.forEach(({ cardBack }) => this.queueImage(cardBack.image.src));
      return this.downloadImages();
    },

    getActivityState() {
      const { rounds } = this.activityData;
      const props = { flipped: false, matched: false };

      return {
        rounds: rounds.map(({ leftColumn, rightColumn, ...round }) => ({
          ...round,
          leftColumn: leftColumn.map(card => ({ ...card, ...props })),
          rightColumn: rightColumn.map(card => ({ ...card, ...props }))
        }))
      };
    },

    getKeyboardConfig() {
      return {
        showKeyboardAtStart: false,
        hideNumberRow: !this.activityData.keyboard.showNumbers
      };
    },

    startActivity() {
      this.setupNewRound();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      TweenMax.set('.flippable-card', { autoAlpha: 1 });
    },

    getCardInColumn({ id }, column) {
      const cards =
        column === 'left' ? this.currentRound.leftColumn : this.currentRound.rightColumn;
      return cards.find(card => card.id === id);
    },

    getFlippedCards() {
      return [...this.currentRound.leftColumn, ...this.currentRound.rightColumn].filter(
        ({ flipped, matched }) => flipped && !matched
      );
    },

    handleCardFlip(card, event) {
      const numberOfCardsFlipped = this.getFlippedCards().length;

      if (card.flipped || this.keyboardEnabled || numberOfCardsFlipped >= 2) return;
      card.flipped = true;

      const { column } = event.currentTarget.dataset;
      const oppositeColumn = column === 'left' ? 'right' : 'left';
      const oppositeCard = this.getCardInColumn(card, oppositeColumn);

      if (oppositeCard.flipped) {
        this.handleMatch(card, oppositeCard);
      }

      if (!oppositeCard.flipped && !!numberOfCardsFlipped) {
        this.handleMismatch();
      }
    },

    handleMismatch() {
      const resetFlippedCards = () =>
        this.getFlippedCards().forEach(card => {
          card.flipped = false;
        });

      const timeline = new TimelineLite();
      timeline.call(resetFlippedCards, null, null, 1);
    },

    handleMatch(card, matchedCard) {
      TweenMax.delayedCall(1, () => {
        this.sndBell();
        this.setCurrentStep({
          step: this.currentRound.rightColumn.findIndex(c => c.id === card.id)
        });
        this.overwriteProgressText({
          currentText: card.answer
        });
        card.matched = true;
        matchedCard.matched = true;
        this.showKeyboard({ enable: true });
      });
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.finishStep();
      }
    },

    finishStep() {
      this.hideKeyboard();
      this.completeStep();

      if (this.isEndOfActivity) {
        this.completeActivity();
      }
    }
  }
};
</script>

<style lang="scss">
.concentration {
  font-family: hwtfutura;

  .top {
    margin-top: 0px;
    margin-left: 10px;

    &.small {
      transform: scale(0.9);
      margin-top: 20px;
    }

    .left,
    .right,
    .card-container {
      display: inline-block;
    }

    & .front-side {
      position: relative;

      .card-text {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        text-align: center;
      }

      span {
        color: black;
        display: block;
        font-family: hwtfutura;
        font-size: 16px;
        position: relative;
        top: 50%;
        transform: translateY(-50%);
      }
    }

    & .back-side {
      img {
        position: absolute;
        left: 25px;
        top: 8px;
        height: 50px;
      }
    }

    .front-side,
    .back-side {
      cursor: pointer;
      position: relative;
    }

    .right {
      vertical-align: top;
      margin: 0 0 0 10px;

      .td-container {
        align-items: center;
        display: flex;
        font-size: 20px;
        height: 68px;
        text-align: left;
      }
    }
  }

  .top.isMobile {
    transform: scale(0.8) !important;
    margin: 10px 0 0 -100px !important;
  }

  .cards-per-column-5 {
    margin-top: 100px;
  }

  .cards-per-column-4 {
    margin-top: 125px;
  }

  .cards-per-column-3 {
    margin-top: 165px;
  }

  .subtitle {
    text-align: center;

    h2 {
      font-size: 36px;
      margin: 0px;
    }
  }

  .fact {
    font-size: 28px;
    margin-top: 20px;
    text-align: center;

    p {
      color: black;
    }
  }
}

.chrome-os {
  .cards-per-column-5 {
    margin-top: 50px;
  }

  .cards-per-column-4 {
    margin-top: 75px;
  }
}
</style>
